// REQ-102 — Modal de detalle completo de un post-it. Carga el detalle real con snapshots
// (useRecognitionDetail) y resuelve nombres de Fundamentales / comportamientos / hábitos contra el
// catálogo. Sin mocks: si el detalle aún no carga, muestra el resumen que ya trae el feed.
//
// REQ-107 / T1 — Acciones de post-it: Editar (SOLO el mensaje), Reportar (texto libre ~100 chars) y
// Eliminar (con confirmación). "Enviar a Brillamos" es una decisión del EMISOR al momento de enviar
// (T5, ver ReconocerPanel.tsx/WordBuilderPanel.tsx) — nunca después, desde el muro de recibidos: eso
// permitiría auto-promoción sin control del emisor. El botón Editar vive AQUÍ, en el modal (decisión
// Adriana 2026-08-07); la tarjeta del muro conserva además su edición
// inline (`PostItWall.tsx`, demo H-23). En ambos lugares solo se edita el MENSAJE — nunca los
// Fundamentales/comportamientos/hábitos. Cada acción aparece SOLO si la config lo permite
// (PostItActionConfig: `canEdit` = GestionSettingsDto.AllowPostEdit, default OFF).
//
// FASE 4 (paridad demo, H-27) — familia `.rec-modal-*` renombrada a `.postit-modal-*` (mapeo 1:1
// con el demo: `.postit-modal-close/-card/-big-postit/-section/-chip-list/-chip/-habit-list/
// -habit-item/-report`), con los valores literales propios del demo (card 480px, close circular
// 34px). El OVERLAY ya no: F5 lo consolidó en `.modal-overlay` porque los quince overlays del
// producto declaraban exactamente lo mismo. `.postit-modal-overlay` sigue en el marcado solo
// como gancho de su excepción de móvil.
import { useEffect, useMemo, useRef, useState } from 'react';
import { nombresDeReceptores, useReparadorDeNombres } from './nombresDeReconocimiento';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  useRecognitionDetail,
  useReportRecognition,
  useDeleteRecognition,
  useUpdateRecognition,
  useReactions,
  useRecognitionRules,
  getFundamentalName,
  getFundamentalColor,
  getHabitName,
  getBehaviorName,
  getRecognitionErrorMessage,
  MENSAJE_MAX_LENGTH,
} from '@/api/recognition';
import type { RecognitionDto, FundamentalDto } from '@/api/recognition';
import {
  formatDate,
  DEFAULT_ACTION_CONFIG,
  PostItTagsArea,
  getPostItPaperColor,
  canReportRecognition,
  shouldShowReactions,
  nombreDelEmisor,
  isEditedMessageValid,
  PuntosDeLaNominacion,
} from './postitShared';
import type { PostItActionConfig } from './postitShared';
import { useVistaHabilitada } from './useVistas';
import { MessageLimitHint } from './messageLimit';
import { useAutoGrowTextarea } from '@/hooks/useAutoGrowTextarea';
import { getFundamentalIcon, getBehaviorIcon } from './fundamentalAssets';

interface Props {
  recognition: RecognitionDto;
  catalog: FundamentalDto[] | undefined;
  onClose: () => void;
  /** Id del usuario actual — habilita eliminar solo sobre reconocimientos propios. */
  currentUserId?: string;
  /** Config de acciones (toggles de OU/campaña). Default: DEFAULT_ACTION_CONFIG. */
  actionConfig?: PostItActionConfig;
  /** Programa del muro que lo abre (Brillamos lo pasa 'Brillamos'): resuelve las reglas efectivas del
   *  programa correcto (p. ej. `minCommentChars`) en vez de caer siempre en Fundamentales. */
  programa?: string | null;
  /** Aviso opcional que se pinta como banner arriba del post-it (p. ej. "la campaña ya finalizó"
   *  cuando se abre desde una notificación de una campaña inactiva). */
  notice?: string;
}

/**
 * RF-086 / RN-M06 — el motivo del reporte es TEXTO LIBRE: nada de catálogo desplegable. El tope lo
 * fija el backend (`ReportRecognitionCommandValidator.MotivoMaxLength`); aquí solo se replica para
 * no dejar escribir de más. El texto viaja crudo, sin traducir: el moderador debe leer exactamente
 * lo que escribió quien reportó, en su idioma.
 */
const REPORT_MOTIVO_MAX = 100;

export function PostItDetailModal({ recognition, catalog, onClose, currentUserId, actionConfig, programa, notice }: Readonly<Props>) {
  const { t, i18n } = useTranslation('recognition');
  const lang = i18n.language;
  const detail = useRecognitionDetail(recognition.id);
  const reactions = useReactions(recognition.id);
  const rules = useRecognitionRules(undefined, programa);
  const minCommentChars = rules.data?.minCommentChars ?? 0;
  const reactionEntries = Object.entries(reactions.data?.counts ?? {})
    .filter(([, count]) => count > 0)
    .sort(([, first], [, second]) => second - first);
  const cfg = actionConfig ?? DEFAULT_ACTION_CONFIG;

  const report = useReportRecognition();
  const del = useDeleteRecognition();
  const update = useUpdateRecognition();

  const [reporting, setReporting] = useState(false);
  const [reportText, setReportText] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draftMessage, setDraftMessage] = useState(recognition.mensaje);
  const draftMessageRef = useRef<HTMLTextAreaElement>(null);
  // Tras guardar, el objeto `recognition` (snapshot del feed) sigue trayendo el texto viejo hasta que
  // el muro refetchee; mostramos el editado al instante para no dejar la sensación de "no guardó".
  const [savedMessage, setSavedMessage] = useState<string | null>(null);

  // Cerrar con Escape.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const funds = catalog ?? [];
  const fundById = new Map(funds.map((f) => [f.id, f]));
  const attitudeById = new Map(funds.flatMap((f) => (f.attitudes ?? []).map((a) => [a.id, a] as const)));
  const habitEntries = new Map(funds.flatMap((f) => (f.dailyHabits ?? []).map((h) => [h.id, { habit: h, fund: f }] as const)));

  const primaryFund = recognition.fundamentalIds.map((id) => fundById.get(id)).find(Boolean);
  const primaryIndex = primaryFund ? funds.indexOf(primaryFund) : 0;
  // Mismo tono pastel que la tarjeta en el muro (`PostItWall.resolvePaper`) — NO el color vibrante
  // del Fundamental (`getFundamentalColor`, pensado para chips/pines): el texto de este post-it
  // ampliado (`.postit-text`) es oscuro y fijo, y sobre un fondo vibrante quedaba ilegible.
  // BUG-01 — resuelto por el COLOR real (`primaryFund.color`), no por `primaryIndex`: ver
  // `getPostItPaperColor` (postitShared.tsx).
  const paper = getPostItPaperColor(primaryFund?.color, primaryIndex);
  const primaryIcon = primaryFund ? getFundamentalIcon(primaryFund, primaryIndex) : null;

  // T4 — defensa propia: "Anónimo" si el flag lo indica o si no vino nombre (no confiar solo en el backend).
  // VIS-6 — nunca un id (ver `nombresDeReconocimiento.ts`).
  const reparar = useReparadorDeNombres(useMemo(() => [recognition], [recognition]));
  const emisor = nombreDelEmisor(recognition, t, reparar);
  const receptores = nombresDeReceptores(recognition, reparar, t('wall.userUnavailable')).join(', ');

  const reportCount = detail.data?.reportCount ?? 0;

  // Propiedad: solo el emisor puede editar/eliminar su propio reconocimiento.
  const isOwner = Boolean(currentUserId && recognition.emisorId && recognition.emisorId === currentUserId);
  const showDelete = cfg.canDelete && isOwner;
  // T1 — Editar solo el mensaje, y solo el emisor cuando la edición está habilitada (cfg.canEdit).
  const showEdit = cfg.canEdit && isOwner;
  // RF-084 — el gate de "Reportar" es el toggle de moderación de la geografía del contenido, que el
  // servidor ya resolvió (`reportEnabled`), no una preferencia de diseño de la campaña.
  // WP-F3 / CPE-158 — a la moderación por geografía (`canReportRecognition`, RF-084) se le suma
  // que la OU ofrezca la vista. Son dos preguntas distintas: «este contenido se puede reportar»
  // y «esta OU ofrece la bandera».
  const verBandera = useVistaHabilitada('banderaReportar');
  const showReport = canReportRecognition(detail.data?.recognition ?? recognition)
    && !isOwner && verBandera;
  const hasActions = showDelete || showReport || showEdit;
  /** Mensaje a mostrar: el recién editado (optimista) o el del snapshot del feed. */
  const displayMessage = savedMessage ?? recognition.mensaje;

  const submitReport = () => {
    if (!reportText.trim()) return;
    report.mutate({ id: recognition.id, body: { motivo: reportText.trim() } }, {
      onSuccess: () => { setReporting(false); setReportText(''); },
    });
  };
  const submitDelete = () => {
    del.mutate(recognition.id, { onSuccess: () => onClose() });
  };
  const startEdit = () => { setDraftMessage(savedMessage ?? recognition.mensaje); update.reset(); setEditing(true); };
  const draftMessageValid = isEditedMessageValid(draftMessage, minCommentChars);
  // Misma caja que las pantallas de envío: 4 líneas de piso, 10 de techo y scroll a partir de ahí.
  useAutoGrowTextarea(draftMessageRef, draftMessage);
  const submitEdit = () => {
    if (!draftMessageValid) return;
    const mensaje = draftMessage.trim();
    update.mutate({ id: recognition.id, body: { mensaje } }, {
      onSuccess: () => { setSavedMessage(mensaje); setEditing(false); },
    });
  };

  // `createPortal` a `document.body`: `PostItWall` (dueño de este modal) se monta a veces dentro
  // de una tarjeta de vidrio `.dash-card`/`.shine-card` (p.ej. la sección "Recientes" de
  // `BrillamosView.tsx` o el muro de `ReconocerView.tsx`), y esas tarjetas llevan
  // `backdrop-filter` — que, por especificación CSS, convierte a ese ancestro en el "contaiing
  // block" de cualquier descendiente `position: fixed`. Sin portal, `.modal-overlay`
  // (`inset: 0`) quedaba encajado en el tamaño de la TARJETA en vez de cubrir la pantalla
  // completa, dejando ver el contenido de abajo. Medido con `getBoundingClientRect()`: sin portal
  // el overlay caía en un rectángulo de ~365×795px en vez de la ventana completa.
  return createPortal((
    <div className="modal-overlay postit-modal-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="postit-modal-card" onClick={(e) => e.stopPropagation()}>
        <button className="postit-modal-close" onClick={onClose} aria-label={t('common.close')}>
          <i className="fas fa-times" />
        </button>

        {notice && (
          <p className="postit-modal-notice" role="status">
            <i className="fas fa-circle-info" aria-hidden="true" /> {notice}
          </p>
        )}

        <div className="postit-modal-big-postit" style={{ backgroundColor: paper }}>
          {primaryIcon && <img src={primaryIcon} alt="" className="postit-fund-icon" />}
          <span className="postit-quote-mark">&ldquo;</span>
          {editing ? (
            <div className="postit-edit-wrap" onClick={(e) => e.stopPropagation()}>
              <textarea className="postit-edit-textarea msg-autogrow" value={draftMessage} rows={4} autoFocus
                ref={draftMessageRef}
                maxLength={MENSAJE_MAX_LENGTH}
                onChange={(event) => setDraftMessage(event.target.value)} />
              <MessageLimitHint length={draftMessage.length} />
              {!draftMessageValid && (
                <p className="postit-edit-hint">
                  {t('actions.editMinChars', { min: minCommentChars, count: draftMessage.trim().length })}
                </p>
              )}
              {update.isError && <p className="rp-error" role="alert">{getRecognitionErrorMessage(update.error, t('actions.editError'))}</p>}
              <div className="postit-edit-actions">
                <button type="button" className="postit-edit-cancel" onClick={() => { setEditing(false); setDraftMessage(displayMessage); update.reset(); }}>{t('common.cancel')}</button>
                <button type="button" className="postit-edit-save" disabled={update.isPending || !draftMessageValid} onClick={submitEdit}>
                  {update.isPending ? t('panel.sending') : t('actions.saveEdit')}
                </button>
              </div>
            </div>
          ) : (
            <p className="postit-text">{displayMessage}</p>
          )}
          <div className="postit-footer postit-footer--visible">
            <span className="postit-from">— {emisor}</span>
            <span className="postit-to">{t('wall.for')} {receptores}</span>
            <div className="postit-meta">
              <span className="postit-date">{formatDate(recognition.fecha, lang)}</span>
              {/* Mismo componente que el muro, no una copia: el post-it se dibuja desde varios
                  sitios y una regla repartida se arregla en uno y se queda rota en los otros. */}
              <PuntosDeLaNominacion recognition={recognition} />
              {recognition.etiquetaGrupal && (
                <span className="postit-group"><i className="fas fa-users" /> {recognition.etiquetaGrupal}</span>
              )}
              <PostItTagsArea recognition={recognition} />
            </div>
          </div>
        </div>

        {recognition.fundamentalIds.length > 0 && (
          <div className="postit-modal-section">
            <h4 className="postit-modal-section-title"><i className="fas fa-book-open" /> {t('wall.fundamentalsRecognized')}</h4>
            <div className="postit-modal-chip-list">
              {recognition.fundamentalIds.map((id) => {
                const f = fundById.get(id);
                const idx = f ? funds.indexOf(f) : 0;
                return (
                  <span key={id} className="postit-modal-chip" style={{ background: f ? getFundamentalColor(f, idx) : '#888' }}>
                    {f && <img src={getFundamentalIcon(f, idx)} alt="" className="postit-modal-chip-icon" />}
                    {f ? getFundamentalName(f, lang) : id}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {recognition.behaviorIds.length > 0 && (
          <div className="postit-modal-section">
            <h4 className="postit-modal-section-title"><i className="fas fa-users-gear" /> {t('wall.behaviorsRecognized')}</h4>
            <div className="postit-modal-chip-list">
              {recognition.behaviorIds.map((id, idx) => {
                const b = attitudeById.get(id);
                return (
                  <span key={id} className="postit-modal-chip postit-modal-chip--behavior">
                    {b && <img src={getBehaviorIcon(b, idx)} alt="" className="postit-modal-chip-icon" />}
                    {b ? getBehaviorName(b, lang) : id}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {recognition.habitIds.length > 0 && (
          <div className="postit-modal-section">
            <h4 className="postit-modal-section-title"><i className="fas fa-bolt" /> {t('wall.habitsRecognized')}</h4>
            <ul className="postit-modal-habit-list">
              {recognition.habitIds.map((id) => {
                const entry = habitEntries.get(id);
                return (
                  <li key={id} className="postit-modal-habit-item">
                    {entry && <span className="postit-modal-habit-fund">{getFundamentalName(entry.fund, lang)}</span>}
                    {entry ? getHabitName(entry.habit, lang) : id}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {shouldShowReactions() && reactionEntries.length > 0 && (
          <div className="postit-modal-section">
            <h4 className="postit-modal-section-title"><i className="far fa-smile" /> {t('reactions.modalTitle')}</h4>
            <div className="postit-modal-reactions">
              {reactionEntries.map(([emoji, count]) => (
                <span key={emoji} className="postit-modal-reaction-chip">{emoji} {count}</span>
              ))}
            </div>
          </div>
        )}

        {detail.isError && <p className="rp-note">{t('wall.detailUnavailable')}</p>}
        {reportCount > 0 && (
          <p className="postit-modal-report"><i className="fas fa-flag" /> {t('wall.reportCount', { count: reportCount })}</p>
        )}

        {/* REQ-107 / T1 — Barra de acciones (según config y propiedad). Oculta mientras se edita:
            el formulario de edición vive dentro del post-it grande. */}
        {hasActions && !editing && (
          <div className="rec-actions">
            {showEdit && (
              <button type="button" className="rec-action-btn" onClick={startEdit}>
                <i className="fas fa-pen" /> {t('actions.edit')}
              </button>
            )}
            {showReport && !reporting && (
              <button type="button" className="rec-action-btn rec-action-btn--warn" onClick={() => setReporting(true)}>
                <i className="fas fa-flag" /> {t('actions.report')}
              </button>
            )}
            {showDelete && !confirmDelete && (
              <button type="button" className="rec-action-btn rec-action-btn--danger" onClick={() => setConfirmDelete(true)}>
                <i className="fas fa-trash" /> {t('actions.delete')}
              </button>
            )}
          </div>
        )}

        {/* RF-086 — motivo en texto libre (sin catálogo). Se envía tal cual lo escribió el reportante. */}
        {reporting && (
          <div className="rec-action-form">
            <label className="rp-label" htmlFor="report-motivo">{t('actions.reportReason')}</label>
            <textarea
              id="report-motivo"
              className="rp-textarea"
              rows={3}
              maxLength={REPORT_MOTIVO_MAX}
              autoFocus
              placeholder={t('actions.reportPlaceholder')}
              aria-describedby="report-motivo-count"
              value={reportText}
              onChange={(e) => setReportText(e.target.value)}
            />
            <p id="report-motivo-count" className="rec-action-count">
              {t('actions.reportCharsLeft', { n: REPORT_MOTIVO_MAX - reportText.length })}
            </p>
            {report.isError && <p className="rp-error" role="alert">{getRecognitionErrorMessage(report.error, t('actions.reportError'))}</p>}
            {report.isSuccess ? (
              <p className="rec-action-ok"><i className="fas fa-check" /> {t('actions.reportSent')}</p>
            ) : (
              <div className="rec-edit-actions">
                <button type="button" className="btn btn--secondary" onClick={() => { setReporting(false); setReportText(''); }}>{t('common.cancel')}</button>
                <button type="button" className="btn btn--send" disabled={report.isPending || !reportText.trim()} onClick={submitReport}>
                  {report.isPending ? t('panel.sending') : t('actions.sendReport')}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Confirmación de eliminación */}
        {confirmDelete && (
          <div className="rec-action-form rec-action-form--danger">
            <p>{t('actions.deleteConfirm')}</p>
            {del.isError && <p className="rp-error" role="alert">{getRecognitionErrorMessage(del.error, t('actions.deleteError'))}</p>}
            <div className="rec-edit-actions">
              <button type="button" className="btn btn--secondary" onClick={() => setConfirmDelete(false)}>{t('common.cancel')}</button>
              <button type="button" className="rec-action-btn rec-action-btn--danger" disabled={del.isPending} onClick={submitDelete}>
                <i className="fas fa-trash" /> {del.isPending ? t('panel.sending') : t('actions.confirmDelete')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  ), document.body);
}
